import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CertiblokDocument } from "../../../interfaces/Document";
import { proxyMap, proxySet } from "valtio/utils";
import { subscribe, useSnapshot } from "valtio";

const TreeViewDocumentPickerContext = createContext<{
	selectedDocuments: Map<string, CertiblokDocument>;
	setSelectedDocuments: (documents: CertiblokDocument[]) => void;
}>({
	selectedDocuments: proxyMap<string, CertiblokDocument>(),
	setSelectedDocuments: () => {},
});

export const TreeViewDocumentPickerContextProvider = ({
	children,
	setSelectedDocuments,
	selectedDocumentsProxy,
}: {
	children: React.ReactNode;
	selectedDocumentsProxy: Map<string, CertiblokDocument>;
	setSelectedDocuments: (documents: CertiblokDocument[]) => void;
}) => {
	return (
		<TreeViewDocumentPickerContext.Provider value={{ selectedDocuments: selectedDocumentsProxy, setSelectedDocuments }}>
			{children}
		</TreeViewDocumentPickerContext.Provider>
	);
};

export const useTreeViewDocumentPickerContext = () => {
	return useContext(TreeViewDocumentPickerContext);
};

export const useInitTreeViewDocumentPicker = () => {
	const [selectedDocumentsProxy] = useState<Map<string, CertiblokDocument>>(proxyMap<string, CertiblokDocument>());

	const setSelectedDocuments = useCallback(
		(documents: CertiblokDocument[]) => {
			selectedDocumentsProxy.clear();
			documents.forEach((document) => {
				selectedDocumentsProxy.set(document.id, document);
			});
		},
		[selectedDocumentsProxy]
	);

	return { selectedDocumentsProxy, setSelectedDocuments };
};

export function useIsPickerDocumentSelected(document?: CertiblokDocument) {
	const { selectedDocuments } = useTreeViewDocumentPickerContext();
	const [isSelected, setIsSelected] = useState(() => selectedDocuments.has(document?.id ?? ""));

	useEffect(() => {
		return subscribe(selectedDocuments, () => {
			if (!document) return;
			setIsSelected(selectedDocuments.has(document.id));
		});
	}, [selectedDocuments, document]);

	return isSelected;
}
